import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import {
  ClinicalAuditEvent,
  ClinicalRecordHistory,
  ClinicalRecordVersionDetail,
} from '../../@types/clinicalRecordAmendment';
import {
  ClinicalRecordAmendmentsService,
  apiErrorMessage,
} from '../../api/clinicalRecordAmendments.service';
import { fDateTime } from '../../utils/formatTime';
import {
  fieldLabel,
  flattenForDisplay,
  formatAuditValue,
  sectionLabel,
} from './amendmentLabels';

type Props = {
  open: boolean;
  clinicalRecordId: number;
  refreshKey?: number;
  onClose: () => void;
};

const service = new ClinicalRecordAmendmentsService();

const EVENT_LABELS: Record<ClinicalAuditEvent['eventType'], string> = {
  RECORD_SIGNED: 'Signed off (original)',
  AMENDMENT_OPENED: 'Unlocked for amendment',
  FIELD_CHANGED: 'Field changed',
  AMENDMENT_SIGNED: 'Amendment re-signed',
};

export default function ClinicalRecordHistoryDialog({
  open,
  clinicalRecordId,
  refreshKey,
  onClose,
}: Props) {
  const [tab, setTab] = useState<'changes' | 'versions' | 'audit'>('changes');
  const [history, setHistory] = useState<ClinicalRecordHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [version, setVersion] = useState<ClinicalRecordVersionDetail | null>(
    null,
  );
  const [versionLoading, setVersionLoading] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await service.getHistory(clinicalRecordId);
      setHistory(data);
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load the change history'));
    } finally {
      setLoading(false);
    }
  }, [clinicalRecordId]);

  useEffect(() => {
    if (open) {
      setVersion(null);
      load();
    }
  }, [open, load, refreshKey]);

  const amendments = useMemo(() => {
    if (!history) return [];
    return history.versions
      .filter((v) => v.kind === 'AMENDMENT')
      .map((v) => ({
        ...v,
        changes: history.events.filter(
          (e) =>
            e.eventType === 'FIELD_CHANGED' &&
            e.versionNumber === v.versionNumber,
        ),
      }))
      .reverse();
  }, [history]);

  const original = history?.versions.find((v) => v.versionNumber === 1);

  const viewVersion = async (versionNumber: number) => {
    setVersionLoading(versionNumber);
    try {
      const { data } = await service.getVersion(
        clinicalRecordId,
        versionNumber,
      );
      setVersion(data);
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load that version'));
    } finally {
      setVersionLoading(null);
    }
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle>
        Clinical record history
        {history && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip
              size="small"
              variant="soft"
              label={`Current version ${history.currentVersion}`}
            />
            {history.isAmended && (
              <Chip
                size="small"
                color="warning"
                variant="soft"
                label="Amended"
              />
            )}
          </Stack>
        )}
      </DialogTitle>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 3 }}>
        <Tab
          value="changes"
          label={`Changes${amendments.length ? ` (${amendments.length})` : ''}`}
        />
        <Tab value="versions" label="Versions" />
        <Tab value="audit" label="Audit trail" />
      </Tabs>
      <Divider />

      <DialogContent sx={{ minHeight: 320 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && history && tab === 'changes' && (
          <Stack spacing={3} sx={{ py: 2 }}>
            {!amendments.length && (
              <Alert severity="info">
                This record has not been amended.
                {original &&
                  ` It was signed off by ${original.signedByName ?? 'the practitioner'} on ${fDateTime(
                    original.signedAt,
                  )}.`}
              </Alert>
            )}
            {amendments.map((amendment) => (
              <Box key={amendment.versionNumber}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  <Typography variant="subtitle1">
                    Version {amendment.versionNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Amended and re-signed by{' '}
                    <strong>{amendment.signedByName ?? 'Unknown user'}</strong>{' '}
                    on {fDateTime(amendment.signedAt)}
                  </Typography>
                </Stack>
                <Alert severity="warning" icon={false} sx={{ my: 1 }}>
                  <strong>Reason:</strong> {amendment.reason}
                  {amendment.reasonPreset &&
                    amendment.reasonPreset !== amendment.reason && (
                      <Typography variant="caption" display="block">
                        Preset: {amendment.reasonPreset}
                      </Typography>
                    )}
                </Alert>
                <ChangesTable changes={amendment.changes} />
              </Box>
            ))}
          </Stack>
        )}

        {!loading && history && tab === 'versions' && (
          <Stack spacing={2} sx={{ py: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Version</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Signed by</TableCell>
                    <TableCell>Signed at</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.versions.map((v) => (
                    <TableRow
                      key={v.versionNumber}
                      selected={version?.versionNumber === v.versionNumber}
                    >
                      <TableCell>{v.versionNumber}</TableCell>
                      <TableCell>
                        {v.kind === 'ORIGINAL'
                          ? 'Original (signed)'
                          : 'Amendment'}
                      </TableCell>
                      <TableCell>{v.signedByName ?? '—'}</TableCell>
                      <TableCell>{fDateTime(v.signedAt)}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        {v.reason ?? '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          disabled={versionLoading !== null}
                          onClick={() => viewVersion(v.versionNumber)}
                        >
                          {versionLoading === v.versionNumber
                            ? 'Loading…'
                            : 'View'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {version && <VersionView version={version} />}
          </Stack>
        )}

        {!loading && history && tab === 'audit' && (
          <TableContainer sx={{ py: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Old value</TableCell>
                  <TableCell>New value</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.events.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {fDateTime(e.changedAt)}
                    </TableCell>
                    <TableCell>
                      {EVENT_LABELS[e.eventType] ?? e.eventType}
                    </TableCell>
                    <TableCell>{e.versionNumber}</TableCell>
                    <TableCell>{e.changedByName ?? '—'}</TableCell>
                    <TableCell>
                      {e.section
                        ? `${sectionLabel(e.section)} › ${fieldLabel(e.fieldPath)}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {e.eventType === 'FIELD_CHANGED'
                        ? formatAuditValue(e.oldValue)
                        : ''}
                    </TableCell>
                    <TableCell>
                      {e.eventType === 'FIELD_CHANGED'
                        ? formatAuditValue(e.newValue)
                        : ''}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      {e.reason ?? ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexGrow: 1, pl: 2 }}
        >
          Generated from the permanent audit log. Entries cannot be edited or
          deleted.
        </Typography>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ChangesTable({ changes }: { changes: ClinicalAuditEvent[] }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Section</TableCell>
            <TableCell>Field</TableCell>
            <TableCell>Old value</TableCell>
            <TableCell>New value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{sectionLabel(c.section)}</TableCell>
              <TableCell>{fieldLabel(c.fieldPath)}</TableCell>
              <TableCell
                sx={{ color: 'error.main', textDecoration: 'line-through' }}
              >
                {formatAuditValue(c.oldValue)}
              </TableCell>
              <TableCell sx={{ color: 'success.dark', fontWeight: 600 }}>
                {formatAuditValue(c.newValue)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function VersionView({ version }: { version: ClinicalRecordVersionDetail }) {
  const sections = Object.entries(version.snapshot?.data ?? {})
    .map(([section, value]) => [section, flattenForDisplay(value)] as const)
    .filter(([, rows]) => rows.length);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <div>
          <Typography variant="subtitle1">
            Version {version.versionNumber}{' '}
            {version.kind === 'ORIGINAL'
              ? '(original signed record)'
              : '(amendment)'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Signed by {version.signedByName ?? '—'} on{' '}
            {fDateTime(version.signedAt)}
          </Typography>
          {version.reason && (
            <Typography variant="body2">Reason: {version.reason}</Typography>
          )}
        </div>
        {version.signatureUrl && (
          <Box
            component="img"
            alt="Signature"
            src={version.signatureUrl}
            sx={{
              maxHeight: 80,
              maxWidth: 240,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'common.white',
            }}
          />
        )}
      </Stack>
      <Divider sx={{ my: 2 }} />
      {!sections.length && (
        <Alert severity="info">
          No clinical data was captured in this version&apos;s snapshot.
        </Alert>
      )}
      {sections.map(([section, rows]) => (
        <Fragment key={section}>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {sectionLabel(section)}
          </Typography>
          <Table size="small">
            <TableBody>
              {rows.map(([path, value]) => (
                <TableRow key={path}>
                  <TableCell sx={{ width: '45%', color: 'text.secondary' }}>
                    {fieldLabel(path)}
                  </TableCell>
                  <TableCell>{formatAuditValue(value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Fragment>
      ))}
    </Box>
  );
}
